import summaryStore from "@/store/summary.store";
import summaryCommonStore from "@/store/summaryCommon.store";
import { BackTraceType } from "@/types/summary";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import SpeakerList from "./SpeakerList";
import TodoItem from "./TodoItem";
import BackTraceIcon from "./BackTraceIcon";
import "./index.less";

export interface BackTraceListRef {
  handleAdd: () => void;
}
interface BackTraceListProps {
  module: "todo";
  itemClassName?: string;
  emptyText: string;
}
const BackTraceList = forwardRef<BackTraceListRef, BackTraceListProps>(
  (props, ref) => {
    const { module, itemClassName, emptyText } = props;
    const summary = summaryStore((state) => state.summary);

    const summaryEditing = summaryCommonStore((state) => state.summaryEditing);
    const setNewBackTraceIds = summaryCommonStore(
      (state) => state.setNewBackTraceIds
    );
    const newBackTraceIds = summaryCommonStore(
      (state) => state.newBackTraceIds
    );
    const newSummary = summaryStore((state) => state.newSummary);
    const modifySummary = summaryStore((state) => state.modifySummary);

    const showBackTrace = summaryCommonStore((state) => state.showBackTrace);
    const backTraceKey = `${module}BackTrace` as BackTraceType;
    const newSummaryContentIds = newBackTraceIds[backTraceKey] || [];
    const newContentIdRef = useRef(-1);
    const selectionRef = useRef<{ id: string; pos: number } | null>(null);
    const summaryContentListRef = useRef(null);
    const isComposing = useRef(false);

    // 退出编辑时需要重置summaryContentIds
    useEffect(() => {
      const srcContentLen = summary?.srcAiGcContent?.[module]?.length || 0;
      summaryCommonStore.getState().setNewBackTraceIds(
        backTraceKey,
        Array.from({ length: srcContentLen }, (_, index) => index.toString())
      );
      newContentIdRef.current = srcContentLen;
    }, [module, summary?.srcAiGcContent?.[module]?.length, summaryEditing]);

    const onItemChange = (
      value: string,
      index: number,
      speakerListStr: string
    ) => {
      if (!newSummary) return;

      modifySummary({
        ...newSummary,
        [module]: newSummary[module].map((item, i) =>
          i === index
            ? `${value}${speakerListStr ? "@" : ""}${speakerListStr}`
            : item
        ),
      });
    };

    const handleAdd = () => {
      if (!newSummary) return;
      // 往最后插入
      const targetIndex = formatArr.length - 1;
      newSummaryContentIds.splice(
        targetIndex + 1,
        0,
        newContentIdRef.current.toString()
      );
      newContentIdRef.current++;
      setNewBackTraceIds(backTraceKey, [...newSummaryContentIds]);
      modifySummary({
        ...newSummary,
        [module]: [...newSummary[module], ""],
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onKeyDown = (
      e: React.KeyboardEvent<HTMLDivElement>,
      index: number
    ) => {
      if (!newSummary) return;
      const newContent = [...newSummary[module]];

      if (e.key === "Backspace") {
        // 没有内容的情况下，按下del键，阻止默认触发onInputChange事件，而是删除该条条目
        if (!e.currentTarget.innerText.trim()) {
          e.preventDefault();
          newContent.splice(index, 1);
          newSummaryContentIds.splice(index, 1);
          setNewBackTraceIds(backTraceKey, [...newSummaryContentIds]);
          const formatNewContent = newContent[index - 1].split("@")[0];
          if (newSummaryContentIds[index - 1]) {
            selectionRef.current = {
              id: `${module}-${newSummaryContentIds[index - 1]}`,
              pos: formatNewContent.length,
            };
          }
          modifySummary({
            ...newSummary,
            [module]: newContent,
          });
        }
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault(); // 阻止默认的换行行为
        // 判断是否正在输入中文
        if (isComposing.current) return;

        const selection = window.getSelection();
        const range = selection?.getRangeAt(0);
        if (!range) return;

        const text = e.currentTarget.innerText;
        const cursorPosition = range.startOffset;
        const beforeEnter = text.substring(0, cursorPosition);
        if (!newSummary?.[module][index]) return;
        const [beforeValue, ...beforeSpeakerList] =
          newSummary[module][index].split("@");
        const afterEnter = text.substring(cursorPosition);
        console.log("beforeEnter", beforeValue);

        newContent[index] = `${beforeEnter}${
          beforeSpeakerList.length > 0 ? "@" : ""
        }${beforeSpeakerList.join("@")}`;
        newContent.splice(index + 1, 0, afterEnter);
        newSummaryContentIds.splice(
          index + 1,
          0,
          newContentIdRef.current.toString()
        );
        newContentIdRef.current++;
        setNewBackTraceIds(backTraceKey, [...newSummaryContentIds]);
        modifySummary({
          ...newSummary,
          [module]: newContent,
        });
      }
    };

    useEffect(() => {
      let observer: MutationObserver | null;
      if (summaryEditing) {
        observer = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === "childList") {
              mutation.addedNodes.forEach((node) => {
                // console.log("node", node, node.nodeType, node.childNodes, node.className);
                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  node.childNodes?.length > 1 &&
                  node.childNodes[1]?.nodeName?.toLowerCase() === "span"
                ) {
                  // 将光标移动到新增的 textarea 的开头
                  const targetTextarea = node
                    .childNodes[1] as HTMLTextAreaElement;
                  targetTextarea.focus();
                }
                else if(node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).className.includes("speaker-item-placeholder")) {
                  const targetTextarea = node as HTMLTextAreaElement;
                  targetTextarea.focus();
                }
              });
              mutation.removedNodes.forEach((node) => {
                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  node.childNodes?.length > 1 &&
                  node.childNodes[1]?.nodeName?.toLowerCase() === "span" &&
                  selectionRef.current
                ) {
                  // 将光标移动到上一个 div 旧内容的末尾
                  const targetDiv = document.getElementById(
                    selectionRef.current.id
                  )?.childNodes?.[1] as HTMLDivElement;
                  if (targetDiv) {
                    targetDiv.focus();
                    const range = document.createRange();
                    const selection = window.getSelection();
                    // 创建一个文本节点的范围
                    if (targetDiv.firstChild) {
                      range.setStart(
                        targetDiv.firstChild,
                        selectionRef.current.pos
                      );
                      range.setEnd(
                        targetDiv.firstChild,
                        selectionRef.current.pos
                      );
                      // 应用选区
                      selection?.removeAllRanges();
                      selection?.addRange(range);
                    }
                  }
                  selectionRef.current = null;
                }
              });
            }
          }
        });
        if (summaryContentListRef.current) {
          observer.observe(summaryContentListRef.current, { childList: true, subtree: true });
        }
      }

      return () => {
        observer?.disconnect();
        observer = null;
      };
    }, [summaryEditing]);

    useImperativeHandle(ref, () => ({
      handleAdd,
    }));

    useEffect(() => {
      if (!showBackTrace) {
        summaryCommonStore.getState().setActiveBackTraceKey("");
      }
    }, [showBackTrace]);

    const onCompositionStart = () => {
      isComposing.current = true;
    };

    const onInput = (
      e: React.ChangeEvent<HTMLDivElement>,
      index: number,
      speakerList: string[]
    ) => {
      const value = e.target.innerText;
      if (isComposing.current) return;
      onItemChange(value, index, speakerList.join("@"));
    };
    const onCompositionEnd = (
      e: React.CompositionEvent<HTMLDivElement>,
      index: number,
      speakerList: string[]
    ) => {
      const value = (e.target as HTMLDivElement).innerText;
      isComposing.current = false;
      onItemChange(value, index, speakerList.join("@"));
    };

    const formatArr =
      (summaryEditing
        ? newSummary?.[module] || summary?.srcAiGcContent?.[module]
        : summary?.srcAiGcContent?.[module]) || [];

    return (
      <div
        ref={summaryContentListRef}
        className="mt-1.5 color-#30343C text-3.5 font-normal text-justify select-text lh-7"
      >
        {!formatArr?.length && !summaryEditing ? (
          <div className="mt-2.5 text-mx-quaternary">{emptyText}</div>
        ) : (
          formatArr?.map((item, index) => {
            const itemId = newSummaryContentIds[index];
            if (!itemId) return null;
            const [formatItem, ...speakerList] = item.split("@");

            return (
              <div
                key={itemId}
                id={`${module}-${itemId}`}
                className={` pl-1 min-h-7 ${
                  index === 0 ? "mt-0" : "mt-2"
                } ${
                  summaryEditing ? "hover-bg-#ECEEF3" : ""
                } rounded-2 color-#30343C`}
              >
                <span className={`lh-7 color-mx-primary`}>{`${
                  index + 1
                }.`}</span>

                <TodoItem
                  itemClassName={itemClassName}
                  contentEditable={summaryEditing}
                  formatItem={formatItem}
                  onInput={(e) => onInput(e, index, speakerList)}
                  onKeyDown={(e) => onKeyDown(e, index)}
                  onCompositionStart={onCompositionStart}
                  onCompositionEnd={(e) =>
                    onCompositionEnd(e, index, speakerList)
                  }
                />

                <SpeakerList
                  module={module}
                  index={index}
                  speakerList={speakerList}
                />
                {!summaryEditing && (
                  <BackTraceIcon
                    module={`todoBackTrace`}
                    index={Number(index)}
                    item={formatItem}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    );
  }
);

export default BackTraceList;
